import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { errorText } from "@common/api";
import { admin, queryKeys, type AdminMember } from "../../api/salvagers";

const label = (member: AdminMember) => member.email ?? `Account ${member.sub.slice(0, 8)}`;

/** The salvagers-admin group: who is in it, add by email, remove. */
export default function Members({ me }: { me: string }) {
  const queryClient = useQueryClient();
  const members = useQuery({ queryKey: queryKeys.members, queryFn: admin.members });
  const [email, setEmail] = useState("");
  const changed = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.members });
    queryClient.invalidateQueries({ queryKey: queryKeys.tables });
  };
  const add = useMutation({
    mutationFn: admin.addMember,
    onSuccess: () => {
      setEmail("");
      changed();
    },
  });
  const remove = useMutation({ mutationFn: admin.removeMember, onSuccess: changed });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (email.trim()) add.mutate(email.trim());
  };
  const error = add.error ?? remove.error ?? members.error;

  return (
    <section>
      <h2>Admins</h2>
      <p className="muted">
        Members of the salvagers-admin group. An account has to exist (sign up at login.kast-dev.net) before it can be
        added.
      </p>
      <form onSubmit={submit} className="row">
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="Email of the account to make an admin"
          aria-label="Email of the account to make an admin"
        />
        <button className="btn primary" type="submit" disabled={add.isPending || !email.trim()}>
          Add
        </button>
      </form>
      {members.data && (
        <ul className="list">
          {members.data.map((member) => (
            <li key={member.sub}>
              <span>{label(member)}</span>
              {member.sub === me ? (
                <span className="muted small">you</span>
              ) : (
                <button
                  className="btn"
                  onClick={() => {
                    if (window.confirm(`Remove admin from ${label(member)}?`)) remove.mutate(member.sub);
                  }}
                  disabled={remove.isPending}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="notice error">{errorText(error, "That didn't work.")}</p>}
    </section>
  );
}
